import React, { FC, ReactElement } from 'react'
import dynamic from 'next/dynamic'
import Event from '@/utils/event'
import { preloadStacks } from '@/utils/wallet/stacks-module'
import { STORAGE_WALLET_TYPE } from '@/utils/constants'
import { WALLET_PROVIDER_KEYS, walletListenersReady } from '@/components/controllers/wallet-listeners'

const PasswordModalController = dynamic(
	() => import('@/components/controllers/password-modal-controller')
)

const SignoutModalController = dynamic(
	() => import('@/components/controllers/signout-modal-controller')
)

const SnackbarController = dynamic(() => import('@/components/controllers/snackbar-controller'))
const ApiContextController = dynamic(
	() => import('@/components/controllers/api-context-controller')
)
const RelistAfterSplitController = dynamic(
	() => import('@/components/controllers/relist-after-split-controller')
)
const PageTrackerController = dynamic(
	() => import('@/components/controllers/page-tracker-controller')
)
const FreezeWatchController = dynamic(
	() => import('@/components/controllers/freeze-watch-controller')
)

const _WalletWarningController = dynamic(
	() => import('@/components/controllers/wallet-warning-modal-controller')
)

const BroadcastModalController = dynamic(
	() => import('@/components/controllers/broadcast-modal-controller')
)

const ListItemModalController = dynamic(
	() => import('@/components/controllers/list-item-modal-controller')
)

const PurchaseModalController = dynamic(
	() => import('@/components/controllers/purchase-modal-controller')
)

const InscriptionDetailModalController = dynamic(
	() => import('@/components/controllers/inscription-detail-modal-controller')
)

const InsufficientFundsModalController = dynamic(
	() => import('@/components/controllers/insufficient-funds-modal-controller')
)

const PwaLaunchController = dynamic(() => import('@/components/controllers/pwa-launch-controller'))

const MoonpayModalController = dynamic(
	() => import('@/components/controllers/moonpay-modal-controller')
)

const _FloatingMenuController = dynamic(
	() => import('@/components/controllers/floating-menu-controller')
)

const GetNotificationsController = dynamic(
	() => import('@/components/controllers/get-notifications-modal-controller')
)

const UnisatController = dynamic(() => import('@/components/controllers/unisat-controller'), {
	ssr: false
})
const InscribeController = dynamic(() => import('@/components/controllers/inscribe-controller'))

function providerSnapshot() {
	const win = window as unknown as Record<string, unknown>
	return Object.fromEntries(WALLET_PROVIDER_KEYS.map((key) => [key, win[key]]))
}

function WalletListeners() {
	const [mounted, setMounted] = React.useState(false)
	React.useEffect(() => {
		let timer = 0
		let poll = 0
		let done = false
		const mount = () => {
			if (done) return
			done = true
			window.clearInterval(poll)
			window.clearTimeout(timer)
			setMounted(true)
		}
		const warmLeather = () => {
			if (window.localStorage.getItem(STORAGE_WALLET_TYPE) === 'hiro') void preloadStacks()
		}
		const onCommand = (payload?: { id?: string }) => {
			if (payload?.id === 'connect-wallet') void preloadStacks()
		}
		warmLeather()
		if (walletListenersReady(window.localStorage, providerSnapshot())) mount()
		// Extensions inject after paint. Safari has no requestIdleCallback, so poll.
		poll = window.setInterval(() => {
			warmLeather()
			if (walletListenersReady(window.localStorage, providerSnapshot())) mount()
		}, 200)
		timer = window.setTimeout(mount, 4000)
		window.addEventListener('pointerdown', mount, { once: true })
		window.addEventListener('keydown', mount, { once: true })
		Event.listen('command', onCommand)
		return () => {
			window.clearInterval(poll)
			window.clearTimeout(timer)
			window.removeEventListener('pointerdown', mount)
			window.removeEventListener('keydown', mount)
			Event.removeListener('command', onCommand)
		}
	}, [])
	if (!mounted) return null
	return <UnisatController />
}

const IncomingOffersController = dynamic(() => import('@/components/offers/incoming-offers-controller'))

const Controllers: FC = (): ReactElement => {
	return (
		<>
			<InscribeController />
			<IncomingOffersController />
			<WalletListeners />
			<PasswordModalController />
			<SignoutModalController />
			<SnackbarController />
			<ApiContextController />
			<RelistAfterSplitController />
			<PageTrackerController />
			<FreezeWatchController />
			{/* <WalletWarningController /> */}
			<GetNotificationsController />
			<BroadcastModalController />
			<ListItemModalController />
			<PurchaseModalController />
			<InscriptionDetailModalController />
			<InsufficientFundsModalController />
			<MoonpayModalController />
			<PwaLaunchController />
			{/* <FloatingMenuController /> */}
		</>
	)
}

export default Controllers
